import React, { useState, useMemo, useEffect } from 'react'
import {
    Box,
    Flex,
    Input,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    IconButton,
    useColorModeValue,
    useToast,
} from '@chakra-ui/react'
import {
    EditIcon,
    CheckIcon,
    DeleteIcon,
    AddIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronUpIcon,
    ChevronDownIcon,
} from '@chakra-ui/icons'

export const DocumentGridMembers = () => {
    // generate 3000 dummy rows on first render
    const [data, setData] = useState(() =>
        Array.from({ length: 3000 }, (_, i) => ({
            id: i + 1,
            firstName: `First${i + 1}`,
            lastName: `Last${i + 1}`,
            email: `user${i + 1}@example.com`,
            isEditing: false,
            draftFirstName: '',
            draftLastName: '',
            draftEmail: '',
        }))
    )

    const [search, setSearch]         = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const pageSize                    = 25
    const [newRowId, setNewRowId]     = useState(null)
    const toast                       = useToast()

    // -- SORT STATE --
    const [sortConfig, setSortConfig] = useState({
        key:    null,      // 'firstName' | 'lastName' | 'email'
        direction: 'asc',  // 'asc' or 'desc'
    })

    const handleSort = columnKey => {
        setSortConfig(sc => {
            if (sc.key === columnKey) {
                // toggle direction
                return {
                    key: columnKey,
                    direction: sc.direction === 'asc' ? 'desc' : 'asc',
                }
            } else {
                // new column, default to ascending
                return { key: columnKey, direction: 'asc' }
            }
        })
        // reset back to page 1 whenever sorting changes
        setCurrentPage(1)
    }

    // -- FILTER --
    const filtered = useMemo(() => {
        const q = search.toLowerCase()
        return data.filter(
            ({ firstName, lastName, email }) =>
                firstName.toLowerCase().includes(q) ||
                lastName.toLowerCase().includes(q) ||
                email.toLowerCase().includes(q)
        )
    }, [data, search])

    // -- SORTING --
    const sortedData = useMemo(() => {
        const { key, direction } = sortConfig
        if (!key) return filtered
        return [...filtered].sort((a, b) => {
            const aVal = a[key].toLowerCase()
            const bVal = b[key].toLowerCase()
            if (aVal < bVal) return direction === 'asc' ? -1 : 1
            if (aVal > bVal) return direction === 'asc' ? 1 : -1
            return 0
        })
    }, [filtered, sortConfig])

    // -- PAGINATION CALC --
    const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize))

    // clamp currentPage if filter/sort shrinks total pages
    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages)
        }
    }, [currentPage, totalPages])

    const startIndex = (currentPage - 1) * pageSize
    const endIndex   = Math.min(startIndex + pageSize, sortedData.length)
    const pagedData  = sortedData.slice(startIndex, endIndex)

    // -- PAGINATION RANGE (with ellipses) --
    const paginationRange = useMemo(() => {
        const boundaryCount = 3
        const siblingCount  = 1
        const totalNumbers  = boundaryCount * 2 + siblingCount * 2 + 1

        if (totalPages <= totalNumbers) {
            return Array.from({ length: totalPages }, (_, i) => i + 1)
        }

        const range = []
        for (let i = 1; i <= totalPages; i++) {
            if (
                i <= boundaryCount ||
                i > totalPages - boundaryCount ||
                (i >= currentPage - siblingCount && i <= currentPage + siblingCount)
            ) {
                range.push(i)
            } else if (range[range.length - 1] !== '...') {
                range.push('...')
            }
        }
        return range
    }, [currentPage, totalPages])

    // theme tokens
    const headerBg    = useColorModeValue('gray.100', 'gray.700')
    const rowBgEven   = useColorModeValue('white',    'gray.800')
    const rowBgOdd    = useColorModeValue('gray.50',  'gray.700')
    const hoverBg     = useColorModeValue('gray.100','gray.600')
    const shadow      = useColorModeValue('sm',       'dark-lg')
    const borderColor = useColorModeValue('gray.200','gray.600')

    // --- existing handlers (edit, save, delete, change, add) ---
    const handleEdit = id => {
        setData(d =>
            d.map(r =>
                r.id === id
                    ? {
                        ...r,
                        isEditing:       true,
                        draftFirstName: r.firstName,
                        draftLastName:  r.lastName,
                        draftEmail:     r.email,
                    }
                    : r
            )
        )
    }

    const handleSave = id => {
        const row = data.find(r => r.id === id)
        if (!row.draftFirstName) {
            return toast({ title: 'Error', description: 'First Name cannot be empty', status: 'error', duration: 3000, isClosable: true })
        }
        if (!row.draftLastName) {
            return toast({ title: 'Error', description: 'Last Name cannot be empty',  status: 'error', duration: 3000, isClosable: true })
        }
        if (!row.draftEmail) {
            return toast({ title: 'Error', description: 'Email cannot be empty',      status: 'error', duration: 3000, isClosable: true })
        }
        setData(d =>
            d.map(r =>
                r.id === id
                    ? {
                        id,
                        firstName:      r.draftFirstName,
                        lastName:       r.draftLastName,
                        email:          r.draftEmail,
                        isEditing:      false,
                        draftFirstName: '',
                        draftLastName:  '',
                        draftEmail:     '',
                    }
                    : r
            )
        )
    }

    const handleDelete = id => {
        setData(d => d.filter(r => r.id !== id))
    }

    const handleChange = (id, field, value) => {
        setData(d =>
            d.map(r =>
                r.id === id
                    ? { ...r, [field]: value }
                    : r
            )
        )
    }

    const handleAddRow = () => {
        setCurrentPage(1)
        const nextId = data.length ? Math.max(...data.map(r => r.id)) + 1 : 1
        setNewRowId(nextId)
        setData(d => [
            {
                id: nextId,
                firstName: '',
                lastName:  '',
                email:     '',
                isEditing: true,
                draftFirstName: '',
                draftLastName:  '',
                draftEmail:     '',
            },
            ...d,
        ])
    }
    // -----------------------------------------------------

    return (
        <Box
            border="1px solid"
            borderColor={borderColor}
            borderRadius="md"
            boxShadow={shadow}
            overflowX="auto"
            bg={useColorModeValue('white','gray.900')}
        >
            {/* controls */}
            <Flex p={4} bg={headerBg} align="center" justify="space-between">
                <Input
                    placeholder="Search by name or email…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    maxW="300px"
                />
                <Button size="sm" leftIcon={<AddIcon />} onClick={handleAddRow}>
                    Add Member
                </Button>
            </Flex>

            {/* summary */}
            <Text fontSize="sm" px={4} py={2}>
                Showing {startIndex + 1}–{endIndex} of {sortedData.length}
            </Text>

            {/* table */}
            <Table size="sm" variant="unstyled" width="75vw" tableLayout="fixed">
                <Thead bg={headerBg}>
                    <Tr>
                        <Th
                            cursor="pointer"
                            onClick={() => handleSort('firstName')}
                        >
                            First Name
                            {sortConfig.key === 'firstName' && (
                                sortConfig.direction === 'asc'
                                    ? <ChevronUpIcon ml={1} />
                                    : <ChevronDownIcon ml={1} />
                            )}
                        </Th>
                        <Th
                            cursor="pointer"
                            onClick={() => handleSort('lastName')}
                        >
                            Last Name
                            {sortConfig.key === 'lastName' && (
                                sortConfig.direction === 'asc'
                                    ? <ChevronUpIcon ml={1} />
                                    : <ChevronDownIcon ml={1} />
                            )}
                        </Th>
                        <Th
                            cursor="pointer"
                            onClick={() => handleSort('email')}
                        >
                            Email
                            {sortConfig.key === 'email' && (
                                sortConfig.direction === 'asc'
                                    ? <ChevronUpIcon ml={1} />
                                    : <ChevronDownIcon ml={1} />
                            )}
                        </Th>
                        <Th textAlign="right" w="120px">Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {pagedData.map((row, idx) => (
                        <Tr
                            key={row.id}
                            bg={idx % 2 === 0 ? rowBgEven : rowBgOdd}
                            _hover={{ bg: hoverBg }}
                        >
                            <Td>
                                {row.isEditing ? (
                                    <Input
                                        size="sm"
                                        autoFocus={row.id === newRowId}
                                        value={row.draftFirstName}
                                        onChange={e =>
                                            handleChange(row.id, 'draftFirstName', e.target.value)
                                        }
                                    />
                                ) : (
                                    <Text isTruncated>{row.firstName}</Text>
                                )}
                            </Td>
                            <Td>
                                {row.isEditing ? (
                                    <Input
                                        size="sm"
                                        value={row.draftLastName}
                                        onChange={e =>
                                            handleChange(row.id, 'draftLastName', e.target.value)
                                        }
                                    />
                                ) : (
                                    <Text isTruncated>{row.lastName}</Text>
                                )}
                            </Td>
                            <Td>
                                {row.isEditing ? (
                                    <Input
                                        size="sm"
                                        value={row.draftEmail}
                                        onChange={e =>
                                            handleChange(row.id, 'draftEmail', e.target.value)
                                        }
                                    />
                                ) : (
                                    <Text isTruncated>{row.email}</Text>
                                )}
                            </Td>
                            <Td textAlign="right">
                                {row.isEditing ? (
                                    <IconButton
                                        size="sm"
                                        icon={<CheckIcon />}
                                        aria-label="Save"
                                        mr={2}
                                        onClick={() => handleSave(row.id)}
                                    />
                                ) : (
                                    <IconButton
                                        size="sm"
                                        icon={<EditIcon />}
                                        aria-label="Edit"
                                        mr={2}
                                        onClick={() => handleEdit(row.id)}
                                    />
                                )}
                                <IconButton
                                    size="sm"
                                    icon={<DeleteIcon />}
                                    aria-label="Delete"
                                    onClick={() => handleDelete(row.id)}
                                />
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>

            {/* pagination */}
            <Flex justify="center" align="center" p={4}>
                <IconButton
                    size="sm"
                    icon={<ChevronLeftIcon />}
                    aria-label="Prev"
                    isDisabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    mr={2}
                />
                {paginationRange.map((p, i) =>
                    p === '...' ? (
                        <Text key={i} mx={2}>…</Text>
                    ) : (
                        <Button
                            key={i}
                            size="sm"
                            mx={1}
                            variant={p === currentPage ? 'solid' : 'outline'}
                            onClick={() => setCurrentPage(p)}
                        >
                            {p}
                        </Button>
                    )
                )}
                <IconButton
                    size="sm"
                    icon={<ChevronRightIcon />}
                    aria-label="Next"
                    isDisabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    ml={2}
                />
            </Flex>
        </Box>
    )
}
